import React from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Textarea } from "../ui/textarea";
import { 
  Flag, 
  Edit, 
  Trash2, 
  Eye, 
  EyeOff, 
  TestTube2, 
  Percent,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle
} from "lucide-react";

// Feature Card Component
export const FeatureCard = ({ feature, onEdit, onDelete }) => (
  <Card className="hover:shadow-md transition-shadow">
    <CardHeader className="pb-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <Flag className={`h-4 w-4 ${feature.is_active ? 'text-green-600' : 'text-gray-400'}`} />
          <CardTitle className="text-base">{feature.display_name}</CardTitle>
        </div>
        <div className="flex space-x-1">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onEdit(feature)}
          >
            <Edit className="h-3 w-3" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete(feature.id, feature.display_name)}
            className="text-red-600 hover:text-red-700"
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        </div>
      </div>
      <CardDescription>{feature.description}</CardDescription>
    </CardHeader>
    <CardContent>
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="text-gray-600">Category</span>
          <p className="font-medium capitalize">{feature.category}</p>
        </div>
        <div>
          <span className="text-gray-600">Required Tier</span>
          <p className="font-medium capitalize">{feature.required_tier}</p>
        </div>
        <div>
          <span className="text-gray-600">Rollout</span>
          <div className="flex items-center space-x-2">
            <p className="font-medium">{feature.rollout_percentage}%</p>
            {feature.rollout_percentage < 100 && (
              <Badge variant="outline" className="text-xs">
                Gradual
              </Badge>
            )}
          </div>
        </div>
        <div>
          <span className="text-gray-600">Status</span>
          <div className="flex items-center space-x-2">
            <Badge variant={feature.is_active ? "default" : "secondary"}>
              {feature.is_active ? "Active" : "Inactive"}
            </Badge>
          </div>
        </div>
        {feature.usage_limit_per_month && (
          <div className="col-span-2">
            <span className="text-gray-600">Usage Limit</span>
            <p className="font-medium">{feature.usage_limit_per_month}/month</p>
          </div>
        )}
      </div>
    </CardContent>
  </Card>
);

// A/B Test Card Component
export const ABTestCard = ({ test }) => {
  const isActive = test.is_active;
  const hasEnded = test.end_date && new Date(test.end_date) < new Date();

  return (
    <Card className={`${isActive && !hasEnded ? 'border-green-200' : 'border-gray-200'}`}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <TestTube2 className={`h-4 w-4 ${isActive ? 'text-green-600' : 'text-gray-400'}`} />
            <CardTitle className="text-base">{test.name}</CardTitle>
          </div>
          <Badge variant={isActive && !hasEnded ? "default" : "secondary"}>
            {hasEnded ? "Ended" : isActive ? "Running" : "Draft"}
          </Badge>
        </div>
        <CardDescription>{test.description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Feature</span>
            <span className="font-medium">{test.feature_name}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Traffic Split</span>
            <span className="font-medium">{test.traffic_split}% to variant B</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Success Metric</span>
            <span className="font-medium">{test.success_metric}</span>
          </div>
          
          <div className="pt-2 border-t">
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className="bg-blue-50 p-2 rounded">
                <p className="font-medium text-blue-900">Variant A (Control)</p>
                <p className="text-blue-700">{test.variant_a}</p>
              </div>
              <div className="bg-green-50 p-2 rounded">
                <p className="font-medium text-green-900">Variant B (Test)</p>
                <p className="text-green-700">{test.variant_b}</p>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

// Feature Dialog Component
export const FeatureDialog = ({ open, onClose, form, setForm, onSubmit, isEditing }) => {
  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Feature Flag' : 'Create New Feature Flag'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="name">Feature Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="advanced_analytics"
                disabled={isEditing}
              />
              <p className="text-xs text-gray-500 mt-1">Unique identifier (no spaces)</p>
            </div>
            
            <div>
              <Label htmlFor="display_name">Display Name *</Label>
              <Input
                id="display_name"
                value={form.display_name}
                onChange={(e) => handleInputChange('display_name', e.target.value)}
                placeholder="Advanced Analytics"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Describe what this feature does"
              rows={3}
            />
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={form.category} onValueChange={(value) => handleInputChange('category', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="analytics">Analytics</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                  <SelectItem value="integrations">Integrations</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                  <SelectItem value="performance">Performance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="required_tier">Required Tier</Label>
              <Select value={form.required_tier} onValueChange={(value) => handleInputChange('required_tier', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="starter">Starter</SelectItem>
                  <SelectItem value="professional">Professional</SelectItem>
                  <SelectItem value="business">Business</SelectItem>
                  <SelectItem value="enterprise">Enterprise</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="rollout_percentage">Rollout %</Label>
              <Input
                id="rollout_percentage"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={form.rollout_percentage}
                onChange={(e) => handleInputChange('rollout_percentage', parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="usage_limit">Monthly Usage Limit (Optional)</Label>
            <Input
              id="usage_limit"
              type="number"
              min="0"
              value={form.usage_limit_per_month || ''}
              onChange={(e) => handleInputChange('usage_limit_per_month', 
                e.target.value ? parseInt(e.target.value) : null)}
              placeholder="Leave empty for unlimited"
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="is_active"
              checked={form.is_active}
              onChange={(e) => handleInputChange('is_active', e.target.checked)}
            />
            <Label htmlFor="is_active">Feature is active</Label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={onSubmit}
              disabled={!form.name || !form.display_name}
            >
              {isEditing ? 'Update Feature' : 'Create Feature'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Configuration Dialog Component
export const ConfigDialog = ({ open, onClose, form, setForm, onSubmit, isEditing }) => {
  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Configuration' : 'Create New Configuration'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="config_key">Configuration Key *</Label>
            <Input
              id="config_key"
              value={form.config_key}
              onChange={(e) => handleInputChange('config_key', e.target.value)}
              placeholder="api_rate_limit"
              disabled={isEditing}
            />
          </div>

          <div>
            <Label htmlFor="config_value">Value *</Label>
            <Input
              id="config_value"
              value={form.config_value}
              onChange={(e) => handleInputChange('config_value', e.target.value)}
              placeholder="100"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="value_type">Value Type</Label>
              <Select value={form.value_type} onValueChange={(value) => handleInputChange('value_type', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="string">String</SelectItem>
                  <SelectItem value="number">Number</SelectItem>
                  <SelectItem value="boolean">Boolean</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="category">Category</Label>
              <Select value={form.category} onValueChange={(value) => handleInputChange('category', value)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="security">Security</SelectItem>
                  <SelectItem value="limits">Limits</SelectItem>
                  <SelectItem value="integrations">Integrations</SelectItem>
                  <SelectItem value="notifications">Notifications</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={form.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="What does this configuration control?"
              rows={2}
            />
          </div>

          <div className="flex items-center space-x-2">
            <input
              type="checkbox"
              id="config_active"
              checked={form.is_active}
              onChange={(e) => handleInputChange('is_active', e.target.checked)}
            />
            <Label htmlFor="config_active">Configuration is active</Label>
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={onSubmit}
              disabled={!form.config_key || !form.config_value}
            >
              {isEditing ? 'Update Configuration' : 'Create Configuration'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// A/B Test Dialog Component
export const ABTestDialog = ({ open, onClose, form, setForm, onSubmit, features }) => {
  const handleInputChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Create New A/B Test</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="test_name">Test Name *</Label>
            <Input
              id="test_name"
              value={form.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="New Dashboard Layout Test"
            />
          </div>

          <div>
            <Label htmlFor="test_description">Description</Label>
            <Textarea
              id="test_description"
              value={form.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Testing new dashboard layout for better user engagement"
              rows={2}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="feature_name">Feature Name</Label>
              <Select value={form.feature_name} onValueChange={(value) => handleInputChange('feature_name', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select feature" />
                </SelectTrigger>
                <SelectContent>
                  {features.map((feature) => (
                    <SelectItem key={feature.id} value={feature.name}>
                      {feature.display_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="traffic_split">Traffic to Variant B (%)</Label>
              <Input
                id="traffic_split"
                type="number"
                min="0"
                max="100"
                step="0.1"
                value={form.traffic_split}
                onChange={(e) => handleInputChange('traffic_split', parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="variant_a">Variant A (Control)</Label>
              <Input
                id="variant_a"
                value={form.variant_a}
                onChange={(e) => handleInputChange('variant_a', e.target.value)}
                placeholder="Current version"
              />
            </div>

            <div>
              <Label htmlFor="variant_b">Variant B (Test)</Label>
              <Input
                id="variant_b"
                value={form.variant_b}
                onChange={(e) => handleInputChange('variant_b', e.target.value)}
                placeholder="New version"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="success_metric">Success Metric</Label>
            <Input
              id="success_metric"
              value={form.success_metric}
              onChange={(e) => handleInputChange('success_metric', e.target.value)}
              placeholder="Click-through rate, conversion rate, etc."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={onSubmit}
              disabled={!form.name || !form.feature_name || !form.variant_a || !form.variant_b}
            >
              Create A/B Test
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};